// TODO move types from apiTypes_deprecated, fix imports
import { Genders, Nationalities } from '@schooly/constants';

import { UserGroupsRequest } from '../groups';
import { AttendanceFrequencyResponse } from '../generated/schooly-api-v2/data-contracts';
import { SimpleListResult } from './endpoints/people';
import { GeneratedGroupType } from './endpoints/timetables';
import { AvailableCriteria, CriteriaUpdate, StringOrNull } from './misc';
import { BaseUserSearchResult } from './school_users';
import { SchoolYear } from './schools';
import { FilterKeys } from './users';

export interface GroupMembership {
  criteria: AvailableCriteria | null;
  is_individual: boolean;
  start: string;
  end: string;
  id: string;
}

export interface GroupValidityUpdate {
  from_school_year_id?: string;
  to_school_year_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface RelationGroup extends GroupBase {
  validity: GroupValidityUpdate;
  memberships: GroupMembership[];
}

export type AvailableRelationGroupFilters = {
  [FilterKeys.Subject]?: string[];
  [FilterKeys.Date]?: string[];
  [FilterKeys.OnlyTutorGroups]?: string[];
};

export type AvailableRelationGroupRequest = {
  filters: AvailableRelationGroupFilters;
  relationId: string;
  search_query?: string;
};

export interface UpdateGroupForRelationRequest extends Pick<UserGroupsRequest, 'relationId'> {
  date_from: string;
  date_to: string;
  group_ids: string[];
}

export enum GroupType {
  Subject = 1,
  TutorGroup = 2,
  NoSubject = 3,
}

export const groupTypeMap: Record<GroupType, GeneratedGroupType> = {
  [GroupType.Subject]: GeneratedGroupType.Value1,
  [GroupType.TutorGroup]: GeneratedGroupType.Value2,
  [GroupType.NoSubject]: GeneratedGroupType.Value3,
};

export interface GroupBase {
  id: string;
  name: string;
  subject?: GroupSubject | null;
  age_groups: GroupAgeGroup[];
  group_type?: GroupType;
  student_count: number;
  user_count?: number;
}

export interface Group extends GroupBase {
  id: string;
  name: string;
  description?: string;
  validity: GroupValidity;
  limited_to_students?: GroupLimitations;
  limited_to_staff?: GroupLimitations;

  students_preview: BaseUserSearchResult[];
  staff_preview: BaseUserSearchResult[];
  staff_count: number;
  student_count: number;

  // DEPRECATED
  area?: number;
  category?: number;
  guardian_count?: number;
  type?: number;
  user_count?: number;
}

export interface GroupWithMembers extends Group {
  students: GroupMember[];
  staff: GroupMember[];
  criteria: AvailableCriteria[];
  has_memberships: boolean;
  can_manage_student_members?: boolean;
}

export interface GroupSubject {
  id: string;
  name: string;
  attendance_default?: boolean;
  order_for_assessments?: number;
  archived?: boolean;
}

export interface GroupAgeGroup {
  id: string;
  name: string;
  order: number;
  archived: boolean;
  attendance_frequency?: AttendanceFrequencyResponse | null;
}

export interface GroupValidity {
  from_school_year?: SchoolYear;
  to_school_year?: SchoolYear;
  date_from?: string;
  date_to?: string;
}

export interface GroupWithMembers extends Group {
  students: GroupMember[];
  staff: GroupMember[];
  criteria: AvailableCriteria[];
  has_memberships: boolean;
  can_manage_student_members?: boolean;
}
export interface GroupLimitations extends Record<string, (string | number)[] | undefined> {
  school_property_ids: string[];
  genders?: Genders[];
  nationalities?: Nationalities[];
  subject_ids?: GroupSubject['id'][];
}

export interface Group extends GroupBase {
  id: string;
  name: string;
  description?: string;
  validity: GroupValidity;
  limited_to_students?: GroupLimitations;
  limited_to_staff?: GroupLimitations;

  attendance_count?: number;
  assessment_published_count?: number;
  can_change_type?: boolean;

  students_preview: BaseUserSearchResult[];
  staff_preview: BaseUserSearchResult[];
  staff_count: number;
  student_count: number;

  timetable_code: StringOrNull;
  default_room_id: StringOrNull;

  // DEPRECATED
  area?: number;
  category?: number;
  guardian_count?: number;
  type?: number;
  user_count?: number;
}

export interface GroupMin extends GroupBase {
  staff_count: number;
  validity: GroupValidity;
  timetable_code: StringOrNull;
  default_room_id: StringOrNull;
  staff_preview: BaseUserSearchResult[];
}

export interface AbsenceRequest {
  comment: string;
  start: string;
  end: string;
}

export interface GroupMember {
  user: SimpleListResult;
  is_actual: boolean;
  is_individual: boolean;
  criteria_ids: string[];
  absence_request: AbsenceRequest;
  age_group_ids?: string[];
}

export interface GroupCategory {
  description: StringOrNull;
  groups: Group[];
  id: string;
  name: string;
}

export interface GroupUpdate {
  name?: string;
  description?: string;
  group_type?: GroupType;
  subject_id?: StringOrNull;
  age_group_ids?: string[];
  validity?: GroupValidityUpdate;
  limited_to_students?: Omit<GroupLimitationsUpdate, 'subject_ids'>;
  limited_to_staff?: GroupLimitationsUpdate;
  staff_ids?: string[];
  student_ids?: string[];
  criteria?: CriteriaUpdate;
  relation_ids_to_remove?: string[];
  timetable_code?: StringOrNull;
  default_room_id?: StringOrNull;
}

export interface GroupLimitationsUpdate extends Record<string, (string | number)[] | undefined> {
  school_property_ids: string[];
  genders?: Genders[];
  nationalities?: Nationalities[];
  subject_ids?: GroupSubject['id'][];
}
